/**
 * The conductor: one grammar for every call, whoever made it.
 *
 *   intent → flight → door → verdict → seal
 *
 * Cues arrive as the data moves (a lift, a request sent, an answer, a sealed
 * commit) and the conductor turns them into tweens, particles, decorations
 * and overlay slips on its own clock. The data path never waits for it:
 * `play` returns at once, the stores have already moved, and what is *shown*
 * lags the store only by the seals that have not played yet.
 *
 * Pacing rules (§4.4):
 *   - a seal for a head this tab caused waits for that call's verdict beat to
 *     start, for at most 300 ms;
 *   - a refusal waits up to 300 ms for the winner's seal at those slots to
 *     play first, so the cause is always seen before the refusal;
 *   - more than 8 queued commits play at 2–4×; more than 40 snap at once
 *     under one wide catch-up ripple;
 *   - a hidden tab snaps and counts what it missed.
 * Reduced motion maps every beat to a short fade (§4.6).
 *
 * Pure: time comes in through `frame(now)` and `now()`; nothing here touches
 * the DOM, GL or audio. Fx adapts the overlay model and the sound events.
 */

import type {
  Attribution, BeatSource, Conductor, Cue, FixIt, Flip, FrameModel, Json, OpClass, Origins, PlaneId, Target, Unsub, WorldMatter,
} from '../contracts';
import { CAM } from './camera';
import { FrameBuilder, SHAPE, cellLeft, maskWords, bitOf, parsePlane, slotsOfMask, type CellAnim, type Decoration, type FxScene, type PackedFrame, type SceneChange } from './frame';
import { DARK_THEME, mixRgb, occupierRgb, tints, toHex, viewerRgb, type Rgb, type Theme } from './palette';
import { MAX_PARTICLES, P_FLOATS, PK, ParticlePool, burst, writeParticle } from './particles';
import { DEFAULT_MOTION, SNAP_BACKLOG, arcPoint, backlogSpeed, clamp, flightMs, motionOf, progress, staggerMs, type Motion } from './tween';
import { isObj, type JsonObject } from '../model/json';
import { PeerLayer, type Peer, type PeerField } from './peers';
import { RippleSet } from './ripples';

type Pt = { x: number; y: number };

// ---------- what the conductor hands the DOM ----------

export type OverlayContent =
  | { readonly kind: 'token'; readonly text: string; readonly color: string }
  | { readonly kind: 'slip'; readonly text: string; readonly ok: boolean }
  | { readonly kind: 'latency'; readonly text: string }
  | { readonly kind: 'sentence'; readonly eyebrow: string; readonly detail: string; readonly subtitle: string | null; readonly fixits: readonly FixIt[]; readonly focus: boolean; readonly plain?: string }
  /** A sentence folded away: a small mark on the refused bits that opens it again. */
  | { readonly kind: 'marker'; readonly label: string }
  | { readonly kind: 'problem'; readonly title: string; readonly detail: string; readonly countdown: string | null }
  | { readonly kind: 'chip'; readonly text: string; readonly count: number }
  | { readonly kind: 'aim'; readonly text: string; readonly color: string };

export type SoundEvent =
  | { readonly kind: 'pluck'; readonly slot: number; readonly pan: number; readonly peer: boolean }
  | { readonly kind: 'thump' }
  | { readonly kind: 'ask'; readonly pan: number }
  | { readonly kind: 'gliss'; readonly pan: number };

export interface CameraLike {
  toScreen(x: number, y: number): Pt;
  toStage(x: number, y: number): Pt;
  pack(out: Float32Array, dpr: number): void;
}

export interface ConductorDeps {
  readonly origins: Origins;
  readonly camera: CameraLike;
  readonly now?: () => number;
  readonly dpr?: () => number;
  /** Where the stage viewport is, in viewport px (for "field edge" and the catch-up ripple). */
  readonly stageRect?: () => { left: number; top: number; width: number; height: number };
}

export interface ConductorImpl extends Conductor {
  /** A new scene; says whether rows moved or were rebuilt (fx crossfades those under reduced motion). */
  setScene(scene: FxScene): SceneChange;
  setTheme(theme: Theme): void;
  setHidden(hidden: boolean): void;
  setQuality(q: number): void;
  /** A peer's name and viewer hue, for aim labels and rings. */
  setPeer(sid: string, info: { name?: string; hue?: number }): void;
  /** A peer left (presence.left): its cursor, aim ring and label go at once. */
  forgetPeer(sid: string): void;
  /** Hold an overlay item while it has focus or the pointer: it neither ages nor fades until let go. */
  hold(id: string, on: boolean): void;
  /** Fold a refusal's sentence away to its mark on the refused bits (a press elsewhere, or its time up). */
  collapse(id: string): void;
  /** Open a folded sentence again from its mark. */
  reopen(markId: string): void;
  /** Anything left to animate: false means the loop may sleep. */
  busy(now: number): boolean;
  /** When a sleeping loop must look again (a slip starts to fade, a shield or trace ends), or null. */
  nextWake(now: number): number | null;
  content(id: string): OverlayContent | undefined;
  dismiss(id: string): void;
  /** Replay what sealed while the tab was hidden, in 600 ms. */
  replayAway(): void;
  /** The opening: the sea condenses toward the stage, then the laid-out Worlds light up in reading order. */
  opening(): void;
  onSound(fn: (e: SoundEvent) => void): Unsub;
  readonly reduced: boolean;
  readonly builder: FrameBuilder;
  /** Seal jobs not yet started (tests and the perf HUD). */
  readonly queued: number;
}

// ---------- internals ----------

interface Beat {
  readonly id: string;
  op: string;
  cls: OpClass | null;
  source: BeatSource;
  target: Target | null;
  world: string | null;
  bits: { world: string; key: number; slots: bigint } | null;
  tint: Rgb; tintEnd: Rgb;
  origin: Pt;
  dest: Pt | null;
  intentAt: number;
  flightAt: number | null;
  flightDur: number;
  landedAt: number | null;
  doorAt: number | null;
  sentAt: number | null;
  verdict: VerdictState | null;
  lastTrail: number;
  pos: Pt;
  endAt: number | null;
  doorShown: boolean;
  /** Made from a verdict alone (no intent or flight was shown): no token to draw. */
  bare: boolean;
}

type VerdictCue = Extract<Cue, { kind: 'receipt' | 'refusal' | 'error' }>;
interface VerdictState { cue: VerdictCue; arrivedAt: number; startedAt: number | null }

interface SealJob {
  readonly flips: readonly Flip[];
  readonly by: Attribution;
  readonly arrivedAt: number;
  readonly commits: number;
  readonly own: 'yes' | 'maybe' | 'no';
  readonly worlds: ReadonlySet<string>;
}

interface Remembered { world: string; key: number; set: bigint; seq: number | null; at: number; by: Attribution; kinds: readonly string[] }

interface Item {
  readonly id: string;
  content: OverlayContent;
  x: number; y: number;
  t0: number;
  life: number;               // ms; Infinity until dismissed
  holdAt: number | null;      // held (focus or pointer inside) since, or null
  readonly fadeIn: number;
  readonly fadeOut: number;
  readonly rise: number;      // px drifted up over its life
  follow: (() => Pt | null) | null;
  scale: number;
}


const WAIT_MS = 300;
const SEAL_SPACING = 180;
const RECENT_MS = 2500;
const SENTENCE_MS = 9000;
/** A sentence let go of (focus or pointer left) keeps at least this long before it fades. */
const HOLD_GRACE_MS = 3000;
/** A peer's cursor and aim go this long after its last frame (a socket that dropped mid-drag sends no `end`). */
/** Under reduced motion the refusal's orb and thread stand still for this long, fading in and out. */
const STILL_THREAD_MS = 1200;
const OWN_KINDS = new Set(['hand', 'director', 'scenario', 'auto']);

const SEQ_OF = (f: Flip): number | null => (f.cause.kind === 'commit' ? f.cause.seq : null);

const dist = (a: Pt, b: Pt): number => Math.hypot(b.x - a.x, b.y - a.y);

/** Read a mask from a target's data: '0x…', a number, or a list of slots. */
export const maskFrom = (v: Json | undefined): bigint | null => {
  if (typeof v === 'string' && /^0x[0-9a-f]+$/i.test(v)) return BigInt(v);
  if (typeof v === 'number' && Number.isInteger(v) && v >= 0) return BigInt(v);
  if (Array.isArray(v)) {
    let m = 0n;
    for (const s of v) if (typeof s === 'number' && Number.isInteger(s) && s >= 0 && s < 4096) m |= 1n << BigInt(s);
    return m;
  }
  return null;
};

const obj = (v: Json | undefined): JsonObject | null => (isObj(v) ? v : null);

/** The address key of a date (and hour, on a minute World) in a World's matter, by its resolved coordinates. */
export const keyOfDate = (wm: WorldMatter | undefined, date: string | undefined, hour?: number): number | null => {
  if (!wm || !date) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
  for (const pm of wm.byKey.values()) {
    const c = pm.coordinates;
    if (pm.parametric !== y || c[0] !== mo || c[1] !== d) continue;
    if (hour !== undefined && c.length > 2 && c[2] !== hour) continue;
    return pm.key;
  }
  return null;
};

export const createConductor = (deps: ConductorDeps): ConductorImpl => {
  const now = deps.now ?? (() => performance.now());
  const builder = new FrameBuilder();
  const pool = new ParticlePool(MAX_PARTICLES);
  const particleOut = new Float32Array((MAX_PARTICLES + 256) * P_FLOATS);
  const cameraOut = new Float32Array(CAM.length);

  let scene: FxScene | null = null;
  let theme: Theme = DARK_THEME;
  let motion: Motion = DEFAULT_MOTION;
  let reduced = false;
  let hidden = false;
  let quality = 0;
  let clock = now();
  let lastFrame = clock;

  const beats = new Map<string, Beat>();
  const byHead = new Map<string, string>();          // receipt head → beat id
  const receiptHeads = new Map<string, number>();    // head → when its receipt beat started
  const queue: SealJob[] = [];
  const pending = new Map<string, Flip[]>();          // world|key → flips not yet shown, in order
  const recent: Remembered[] = [];
  let nextSealAt = 0;
  let burstSpeed = 1;
  let decorations: (Decoration & { until: number; t0: number; pulse: boolean })[] = [];
  const items = new Map<string, Item>();
  const ripples = new RippleSet();
  const peers = new PeerLayer((v) => maskFrom(v as Json | undefined));
  const soundFns = new Set<(e: SoundEvent) => void>();
  let away = { commits: 0, flips: [] as Flip[] };
  let lastRecheck = clock;
  let openingDue = false;

  // ---------- shown = store with unplayed flips undone ----------

  const shownMask = (world: string, key: number, plane: PlaneId, stored: bigint): bigint => {
    const list = pending.get(`${world}|${key}`);
    if (!list) return stored;
    let m = stored;
    for (let i = list.length - 1; i >= 0; i--) {
      const f = list[i] as Flip;
      if (f.plane === plane) m = (m & ~f.set) | f.clear;
    }
    return m;
  };
  /** The planes unplayed flips touch at a position: a store drops a plane its last release emptied, but the eye has not seen that yet. */
  const pendingPlanes = (world: string, key: number): PlaneId[] | undefined => {
    const list = pending.get(`${world}|${key}`);
    if (!list) return undefined;
    const out: PlaneId[] = [];
    for (const f of list) if (!out.includes(f.plane)) out.push(f.plane);
    return out;
  };
  builder.setShown(shownMask, pendingPlanes);

  const addPending = (f: Flip) => {
    const k = `${f.world}|${f.key}`;
    const list = pending.get(k);
    if (list) list.push(f); else pending.set(k, [f]);
  };
  const dropPending = (f: Flip) => {
    const k = `${f.world}|${f.key}`;
    const list = pending.get(k);
    if (!list) return;
    const i = list.indexOf(f);
    if (i >= 0) list.splice(i, 1);
    if (list.length === 0) pending.delete(k);
  };

  // ---------- small helpers ----------

  /** Heads are remembered for attribution and pacing, the last 512 of them. */
  const HEADS_KEPT = 512;
  const keep = <V>(m: Map<string, V>, k: string, v: V) => {
    m.delete(k);
    m.set(k, v);
    if (m.size > HEADS_KEPT) m.delete(m.keys().next().value as string);
  };

  const sound = (e: SoundEvent) => {
    if (hidden) return;
    for (const fn of soundFns) fn(e);
  };

  const stage = () => deps.stageRect?.() ?? { left: 0, top: 0, width: 1280, height: 800 };
  const centre = (): Pt => {
    const s = stage();
    return { x: s.left + s.width / 2, y: s.top + s.height / 2 };
  };

  const layoutOf = (world: string) => scene?.layouts.find((l) => l.world === world);

  /** The centre of some bits of a World, in viewport px (null when the World is not laid out). */
  const bitsCentre = (world: string, key: number, slots: bigint): Pt | null => {
    const list = slotsOfMask(slots, 4096);
    const a = builder.cellRect(world, key, list[0] ?? 0);
    const b = builder.cellRect(world, key, list[list.length - 1] ?? 0);
    if (!a || !b) return null;
    return deps.camera.toScreen((a.x + b.x + b.w) / 2, a.y + a.h / 2);
  };

  const worldPoint = (world: string | null | undefined): Pt | null => {
    if (!world) return null;
    const o = deps.origins.at(`world:${world}`);
    if (o) return o;
    const L = layoutOf(world);
    if (!L) return null;
    const top = L.rows[0]?.y ?? 0, bottom = (L.rows[L.rows.length - 1]?.y ?? 0) + L.cell;
    return deps.camera.toScreen(L.x + (L.slots * (L.cell + L.gap)) / 2, L.y + (top + bottom) / 2);
  };

  const fieldEdge = (world: string | null | undefined): Pt => {
    const L = world ? layoutOf(world) : undefined;
    if (L) {
      const mid = L.y + ((L.rows[0]?.y ?? 0) + (L.rows[L.rows.length - 1]?.y ?? 0)) / 2;
      const p = deps.camera.toScreen(L.x, mid);
      return { x: p.x - 40, y: p.y };
    }
    // No field on screen: a point in from the left, clear of the tool rail, where a screen's content sits.
    const s = stage();
    return { x: s.left + Math.min(s.width * 0.22, 320), y: s.top + s.height * 0.42 };
  };

  /** The bits a target names, when it names some: data {key, slots} or {date, hour, slots}. */
  const targetBits = (t: Target | null): { world: string; key: number; slots: bigint } | null => {
    if (!t?.world) return null;
    const d = obj(t.data);
    if (!d) return null;
    const slots = maskFrom(d.slots ?? d.mask ?? (typeof d.slot === 'number' ? [d.slot] : undefined));
    let key: number | null = typeof d.key === 'number' ? d.key : null;
    if (key === null && typeof d.date === 'string') key = keyOfDate(scene?.matter(t.world), d.date, typeof d.hour === 'number' ? d.hour : undefined);
    if (key === null || slots === null) return null;
    return { world: t.world, key, slots };
  };

  const FIELD_KINDS = new Set(['bits', 'field', 'row', 'header', 'tick', 'leg', 'braid']);
  const destOf = (t: Target | null): Pt | null => {
    const o = deps.origins;
    // A call that names no World lands in the middle of the stage's ground when the stage is up (listWorlds), else at the door.
    if (!t) return o.at('ground') ?? o.at('gate:door');
    if (t.kind.startsWith('gate:')) return o.at(t.kind) ?? o.at(t.id) ?? worldPoint(t.world);
    if (FIELD_KINDS.has(t.kind)) {
      const b = targetBits(t);
      // A World not laid out as a field here (a ring on the stage): the call flies to that World, not to a door far away.
      if (t.world && !layoutOf(t.world)) {
        const ring = o.at(`world:${t.world}`);
        if (ring) return ring;
      }
      return o.at('gate:door') ?? (b ? bitsCentre(b.world, b.key, b.slots) : null) ?? o.at(t.id) ?? worldPoint(t.world);
    }
    return o.at(t.id) ?? o.at(`${t.kind}:${t.world ?? ''}`) ?? worldPoint(t.world);
  };

  const peerPoint = (sid: string): Pt | null => {
    const o = deps.origins.at(`peer:${sid}`);
    if (o) return o;
    const p = peers.get(sid);
    return p?.shown ? deps.camera.toScreen(p.shown.x, p.shown.y) : null;
  };

  /** A conflicting occupier's orb: by its id (the contract's `orb:<w>:<occupierId>`) when the scene can name it, else by name. */
  const orbOf = (world: string, name: string): Pt | null => {
    const id = scene?.occupierId?.(world, name);
    return (typeof id === 'number' ? deps.origins.at(`orb:${world}:${id}`) : null) ?? deps.origins.at(`orb:${world}:${name}`);
  };

  const originOf = (s: BeatSource, world: string | null): Pt => {
    const o = deps.origins;
    switch (s.kind) {
      case 'me': return s.at ?? o.at('ghost') ?? centre();
      case 'peer': return peerPoint(s.sid) ?? fieldEdge(world);
      case 'director': return o.at('director') ?? fieldEdge(world);
      case 'scenario': return o.at('ghost') ?? fieldEdge(world);
      case 'road': return o.at(`world:${s.from}`) ?? fieldEdge(world);
      case 'unknown': return fieldEdge(world);
    }
  };

  const tintOf = (s: BeatSource): [Rgb, Rgb] => {
    const t = tints(theme);
    switch (s.kind) {
      case 'me': return [t.me, t.meEnd];
      case 'peer': return [viewerRgb(theme, s.hue), viewerRgb(theme, s.hue)];
      case 'director': return [t.director, t.director];
      case 'scenario': return [t.scenario, t.scenario];
      case 'road': return [t.roadOut, t.roadBack];
      case 'unknown': return [t.unknown, t.unknown];
    }
  };

  const tokenLabel = (b: Beat): string => (b.source.kind === 'unknown' ? `${b.op} · API` : b.op);

  /** A refusal's sentence, kept while it or its mark is up: which World it speaks of, and what it says. */
  const sentences = new Map<string, { world: string; key: number; slots: bigint; content: Extract<OverlayContent, { kind: 'sentence' }> }>();
  const markOf = (sen: string): string => `mk:${sen.slice(4)}`;
  const forget = (sen: string): void => {
    items.delete(sen);
    items.delete(markOf(sen));
    sentences.delete(sen);
  };
  const foldAway = (sen: string): void => {
    const it = items.get(sen);
    const meta = sentences.get(sen);
    items.delete(sen);
    if (!meta) return;
    const at = bitsCentre(meta.world, meta.key, meta.slots) ?? (it ? { x: it.x, y: it.y } : null);
    // It stands still and asks for no frames: the loop may sleep with it up.
    if (at) addItem(markOf(sen), { kind: 'marker', label: meta.content.eyebrow }, at, { life: Infinity, fadeIn: 0 });
  };

  const addItem = (id: string, content: OverlayContent, at: Pt, o: { life: number; fadeIn?: number; fadeOut?: number; rise?: number; follow?: (() => Pt | null) | null }) => {
    items.set(id, {
      id, content, x: at.x, y: at.y, t0: clock, life: o.life, holdAt: null,
      fadeIn: o.fadeIn ?? (reduced ? 0 : motion.quick), fadeOut: o.fadeOut ?? motion.quick,
      rise: reduced ? 0 : o.rise ?? 0, follow: o.follow ?? null, scale: 1,
    });
  };

  const addRipple = (r: Parameters<RippleSet['add']>[0]) => {
    if (reduced || hidden || quality >= 2) return;
    ripples.add(r, clock);
  };

  const decorate = (d: Decoration, ms: number, pulse = false) => {
    decorations.push({ ...d, until: clock + ms, t0: clock, pulse });
  };

  const beatOf = (id: string, fallback: () => Beat): Beat => {
    let b = beats.get(id);
    if (!b) {
      b = fallback();
      beats.set(id, b);
    }
    return b;
  };

  const newBeat = (id: string, op: string, source: BeatSource, target: Target | null, cls: OpClass | null): Beat => {
    const world = target?.world ?? null;
    const [tint, tintEnd] = tintOf(source);
    const origin = originOf(source, world);
    return {
      id, op, cls, source, target, world, bits: targetBits(target), tint, tintEnd, origin, dest: null,
      intentAt: clock, flightAt: null, flightDur: 0, landedAt: null, doorAt: null, sentAt: null,
      verdict: null, lastTrail: 0, pos: { ...origin }, endAt: null, doorShown: false, bare: false,
    };
  };

  const mine = (b: Beat): boolean => b.source.kind === 'me' || b.source.kind === 'director' || b.source.kind === 'scenario';

  /**
   * A beat of this tab still waiting for its verdict on a World, that could
   * have made the commit `head`. A refused or failed call wrote nothing, and a
   * receipt that names another head made another commit: neither can own the
   * seal, so neither holds it back (the loser's refusal is itself waiting for
   * that seal).
   */
  const awaitingVerdict = (worlds: ReadonlySet<string>, head?: string): boolean => {
    for (const b of beats.values()) {
      if (!mine(b) || b.flightAt === null) continue;
      const v = b.verdict;
      if (v) {
        if (v.startedAt !== null) continue;
        if (v.cue.kind !== 'receipt') continue;
        if (head !== undefined && v.cue.head !== null && v.cue.head !== head) continue;
      }
      if (b.world === null || worlds.has(b.world)) return true;
    }
    return false;
  };

  // ---------- seals ----------

  const commitsIn = (flips: readonly Flip[]): number => {
    const seqs = new Set<string>();
    for (const f of flips) seqs.add(f.cause.kind === 'commit' ? `${f.world}|${f.cause.seq}` : `${f.world}|${f.cause.kind}`);
    return Math.max(1, seqs.size);
  };

  const backlog = (): number => queue.reduce((n, j) => n + j.commits, 0);

  const mayStart = (job: SealJob): boolean => {
    if (job.own === 'no') return true;
    if (receiptHeads.has(job.by.head)) return true;
    if (clock - job.arrivedAt >= WAIT_MS) return true;
    if (job.own === 'maybe' && !awaitingVerdict(job.worlds, job.by.head)) return true;
    return false;
  };

  const planeRgb = (world: string, plane: PlaneId): Rgb => {
    const { id, role } = parsePlane(plane);
    if (role === 'proposed') return theme.ask;
    const wm = scene?.matter(world);
    if (wm?.kinds.get(id) === 'foreign') return theme.foreign;
    return occupierRgb(theme, scene?.hue(world, plane) ?? 0);
  };

  /** Where a seal's stagger starts: the drop point, the Director's orb, the ghost hand, the peer's cursor. */
  const impactOf = (job: SealJob): Pt | null => {
    const src = job.by.source;
    const beatId = byHead.get(job.by.head);
    const b = beatId ? beats.get(beatId) : undefined;
    if (b) return b.source.kind === 'me' ? b.origin : b.dest ?? b.origin;
    if (src.kind === 'director') return deps.origins.at('director');
    if (src.kind === 'scenario') return deps.origins.at('ghost');
    if (src.kind === 'peer') return peerPoint(src.sid);
    return null;
  };

  const remember = (f: Flip, by: Attribution) => {
    const kinds = f.cause.kind === 'commit' ? [f.cause.commitKind] : [f.cause.kind];
    recent.push({ world: f.world, key: f.key, set: f.set, seq: SEQ_OF(f), at: clock, by, kinds });
    while (recent.length > 256) recent.shift();
  };

  /** Show some flips: the rows change look now, and (unless snapping) each changed cell animates. */
  const showFlips = (flips: readonly Flip[], by: Attribution | null, how: 'seal' | 'snap' | 'correct' | 'replay', speed = 1, impact: Pt | null = null) => {
    const reveal = motion.reveal / speed;
    const byRow = new Map<string, { world: string; key: number; set: bigint; clear: bigint; planes: Map<PlaneId, { set: bigint; clear: bigint }> }>();
    for (const f of flips) {
      if (how !== 'replay') dropPending(f);
      if (by) remember(f, by);
      const k = `${f.world}|${f.key}`;
      let e = byRow.get(k);
      if (!e) byRow.set(k, (e = { world: f.world, key: f.key, set: 0n, clear: 0n, planes: new Map() }));
      e.set |= f.set;
      e.clear |= f.clear;
      const p = e.planes.get(f.plane) ?? { set: 0n, clear: 0n };
      e.planes.set(f.plane, { set: p.set | f.set, clear: p.clear | f.clear });
    }
    // the impact cell, per World
    const impactCell = new Map<string, { slot: number; row: number }>();
    for (const e of byRow.values()) {
      if (impactCell.has(e.world)) continue;
      const L = layoutOf(e.world);
      if (!L) continue;
      let cell: { slot: number; row: number } | null = null;
      if (impact) {
        const s = deps.camera.toStage(impact.x, impact.y);
        const slot = clamp(Math.floor((s.x - L.x) / (L.cell + L.gap)), 0, L.slots - 1);
        let row = 0, best = Infinity;
        L.rows.forEach((r, i) => {
          const dy = Math.abs(L.y + r.y + L.cell / 2 - s.y);
          if (dy < best) { best = dy; row = i; }
        });
        cell = { slot, row };
      }
      if (!cell) {
        const first = slotsOfMask(e.set || e.clear, L.slots)[0] ?? 0;
        const row = Math.max(0, builder.rowsAt(e.world, e.key)[0]?.index ?? 0);
        cell = { slot: first, row };
      }
      impactCell.set(e.world, cell);
    }

    let firstSet: { world: string; key: number; slot: number; plane: PlaneId } | null = null;
    for (const e of byRow.values()) {
      const rows = builder.rowsAt(e.world, e.key);
      if (rows.length === 0) continue;
      const ic = impactCell.get(e.world) ?? { slot: 0, row: 0 };
      for (const r of rows) {
        let set = 0n, clear = 0n;
        if (r.plane === null) { set = e.set; clear = e.clear; }
        else {
          const p = e.planes.get(r.plane);
          if (!p) continue;
          set = p.set; clear = p.clear;
        }
        const changed = set | clear;
        if (changed === 0n) continue;
        const slots = r.layout.slots;
        const before = builder.capture(r.id);
        if (how === 'snap' && !before) continue;
        const cellsSet = new Uint8Array(slots), cellsClear = new Uint8Array(slots);
        const delays = new Float32Array(slots);
        const ws = maskWords(set, slots), wc = maskWords(clear, slots);
        for (let s = 0; s < slots; s++) {
          if (bitOf(ws, s)) cellsSet[s] = 1;
          else if (bitOf(wc, s)) cellsClear[s] = 1;
          const d = Math.abs(s - ic.slot) + Math.abs(r.index - ic.row);
          delays[s] = reduced || how !== 'seal' ? 0 : staggerMs(d, motion) / speed;
        }
        if (how === 'snap') {
          builder.animate(r.id, { kind: 'fade', cells: Uint8Array.from(cellsSet, (v, i) => v | (cellsClear[i] as number)), delays: new Float32Array(slots), t0: clock, dur: 200, before });
          continue;
        }
        if (how === 'correct') {
          builder.animate(r.id, { kind: 'correct', cells: Uint8Array.from(cellsSet, (v, i) => v | (cellsClear[i] as number)), delays, t0: clock, dur: reduced ? 80 : 520, before });
          continue;
        }
        if (reduced) {
          builder.animate(r.id, { kind: 'fade', cells: Uint8Array.from(cellsSet, (v, i) => v | (cellsClear[i] as number)), delays, t0: clock, dur: 80, before });
        } else {
          const ignite: CellAnim = { kind: 'ignite', cells: cellsSet, delays, t0: clock, dur: reveal, before: how === 'replay' ? null : before };
          builder.animate(r.id, ignite);
          if (cellsClear.some((v) => v)) builder.animate(r.id, { kind: 'fade', cells: cellsClear, delays: new Float32Array(slots), t0: clock, dur: Math.min(reveal, 240), before });
        }
        // embers: cleared bits fall 12 px and fade (main rows only)
        if (r.plane === null && !reduced) {
          for (const [plane, p] of e.planes) {
            if (p.clear === 0n) continue;
            const rgb = planeRgb(e.world, plane);
            for (const s of slotsOfMask(p.clear, slots)) {
              const x = cellLeft(r.layout, s) + r.layout.cell / 2, y = r.y + r.layout.cell / 2;
              for (let k = 0; k < 2; k++) {
                pool.spawn({ x: x + (k - 0.5) * r.layout.cell * 0.3, y, vy: (12 / reveal) * 1000, drag: 1, size: r.layout.cell * 0.16, size1: r.layout.cell * 0.05, life: reveal, delay: k * 60, kind: PK.dot, stage: true, rgb, alpha: 0.9 });
              }
            }
          }
        }
        if (reduced) {
          decorate({ world: e.world, key: e.key, slots: changed, shape: SHAPE.highlight, rgb: theme.focus, alpha: 0.9, glow: 0, phase: 0 }, 1000);
        }
        if (!firstSet && r.plane === null) {
          for (const [plane, p] of e.planes) {
            if (p.set === 0n) continue;
            const s = slotsOfMask(p.set, slots)[0];
            if (s !== undefined) { firstSet = { world: e.world, key: e.key, slot: s, plane }; break; }
          }
        }
      }
      builder.invalidate(e.world, e.key);
    }
    return firstSet;
  };

  const startSeal = (job: SealJob, speed: number) => {
    const impact = impactOf(job);
    const first = showFlips(job.flips, job.by, 'seal', speed, impact);
    const world = job.flips[0]?.world ?? null;
    const at = worldPoint(world);
    const L = world ? layoutOf(world) : undefined;
    const rgb = first ? planeRgb(first.world, first.plane) : theme.mine;
    if (L && at) {
      // ripple on the World itself (stage space), so it follows the camera
      const s = deps.camera.toStage(at.x, at.y);
      addRipple({ space: 'stage', x: s.x, y: s.y, hue: first ? scene?.hue(first.world, first.plane) ?? 178 : 178, rgb, width: 70, strength: 1 });
    } else if (at) {
      addRipple({ space: 'screen', x: at.x, y: at.y, hue: 178, rgb, width: 70, strength: 1 });
    }
    const kinds = new Set(job.flips.map((f) => (f.cause.kind === 'commit' ? f.cause.commitKind : f.cause.kind)));
    const peer = job.by.source.kind === 'peer';
    const pan = panOf(at);
    if (kinds.has('displace')) sound({ kind: 'gliss', pan });
    if (kinds.has('propose')) sound({ kind: 'ask', pan });
    if (first && parsePlane(first.plane).role === 'held') sound({ kind: 'pluck', slot: first.slot, pan: peer ? pan : 0, peer });
  };

  const panOf = (p: Pt | null): number => {
    if (!p) return 0;
    const s = stage();
    return clamp(((p.x - s.left) / Math.max(1, s.width)) * 2 - 1, -1, 1);
  };

  const snapQueue = (catchUp: boolean) => {
    if (queue.length === 0) return;
    const all = queue.splice(0, queue.length);
    for (const job of all) showFlips(job.flips, job.by, 'snap');
    if (catchUp) {
      const c = centre();
      addRipple({ space: 'screen', x: c.x, y: c.y, hue: 178, rgb: theme.mine, width: 260, strength: 1.4 });
    }
  };

  // ---------- verdicts ----------

  const touching = (world: string, key: number, slots: bigint) => (r: { world: string; key: number; set: bigint }) =>
    r.world === world && r.key === key && (r.set & slots) !== 0n;

  const heldShown = (world: string, key: number, slots: bigint): boolean => {
    const wm = scene?.matter(world);
    const pm = wm?.byKey.get(key);
    if (!pm) return false;
    const held = (plane: PlaneId, stored: bigint) => parsePlane(plane).role === 'held' && (shownMask(world, key, plane, stored) & slots) !== 0n;
    for (const [plane, stored] of pm.planes) if (held(plane, stored)) return true;
    for (const plane of pendingPlanes(world, key) ?? []) if (!pm.planes.has(plane) && held(plane, 0n)) return true;
    return false;
  };

  /** A refusal plays once the winner's seal at its slots has (or 300 ms have passed). */
  const refusalReady = (b: Beat): boolean => {
    const v = b.verdict;
    if (!v || v.cue.kind !== 'refusal') return true;
    const c = v.cue;
    if (c.by !== 'held' && c.by !== 'capacity') return true;
    if (clock - v.arrivedAt >= WAIT_MS) return true;
    const t = touching(c.world, c.key, c.slots);
    if (queue.some((j) => j.flips.some((f) => t({ world: f.world, key: f.key, set: f.set })))) return false;
    if (recent.some((r) => t(r) && clock - r.at <= RECENT_MS)) return true;
    return heldShown(c.world, c.key, c.slots);
  };

  const whoSealed = (by: Attribution): string => {
    const s = by.source;
    switch (s.kind) {
      case 'peer': {
        const n = peers.get(s.sid)?.name;
        return n ? `from ${n}'s tab (unverified)` : "from a peer's tab (unverified)";
      }
      case 'director': return 'by the Director';
      case 'scenario': return 'by the scenario';
      case 'hand': case 'auto': return 'from this tab';
      default: return 'by another client';
    }
  };

  const startVerdict = (b: Beat) => {
    const v = b.verdict;
    if (!v || v.startedAt !== null) return;
    v.startedAt = clock;
    b.endAt = clock + (reduced ? 120 : motion.quick);
    const at = b.dest ?? b.pos;
    const c = v.cue;
    items.delete(`lat:${b.id}`);
    if (c.kind === 'receipt') {
      if (c.head) keep(receiptHeads, c.head, clock);
      const seq = c.head ? recent.find((r) => r.by.head === c.head)?.seq ?? queue.find((j) => j.by.head === c.head)?.flips.map(SEQ_OF).find((s) => s !== null) ?? null : null;
      const ok = c.status >= 200 && c.status < 300;
      const cls = b.cls;
      const tail = cls === 'read' ? 'read' : cls === 'delete' ? 'gone' : cls === 'socket' ? 'live' : cls === 'stream' ? 'stream' : null;
      const text = [String(c.status), seq !== null ? `#${seq}` : null, c.head ? c.head.slice(0, 8) : null, tail].filter(Boolean).join(' · ');
      addItem(`slip:${b.id}`, { kind: 'slip', text, ok }, at, { life: 1500, rise: 14 });
      if (!reduced) {
        if (cls === 'delete') burst(pool, at, 10, { speed: 90, size: 3, life: 520, kind: PK.shard, rgb: theme.foreign, seed: hashSeed(b.id) });
        else if (cls === 'read') pool.spawn({ x: at.x, y: at.y, size: 6, size1: 34, life: 420, kind: PK.ring, rgb: theme.focus, alpha: 0.8 });
        else {
          pool.spawn({ x: at.x, y: at.y, size: 6, size1: 46, life: 520, kind: PK.ring, rgb: b.tintEnd, alpha: 0.9 }, true);
          burst(pool, at, 8, { speed: 150, size: 3.2, life: 420, kind: PK.spark, rgb: b.tintEnd, seed: hashSeed(b.id) });
        }
      }
      if (cls !== 'read' && cls !== 'delete') addRipple({ space: 'screen', x: at.x, y: at.y, hue: 178, rgb: b.tintEnd, width: 50, strength: 0.6 });
    } else if (c.kind === 'refusal') {
      // a ring and a cross, never a fill: the glow stays faint so it does not read as one
      const shield: Decoration = { world: c.world, key: c.key, slots: c.slots, shape: SHAPE.shield, rgb: theme.refuse, alpha: 1, glow: 0.08, phase: 0, inflate: 2 };
      decorate(shield, SENTENCE_MS / 3, !reduced);
      const shieldAt = bitsCentre(c.world, c.key, c.slots);
      if (!reduced) burst(pool, at, 10, { speed: 170, size: 3.6, life: 520, kind: PK.shard, rgb: theme.refuse, seed: hashSeed(b.id), gravity: 160 });
      // the cause, named: each conflicting orb pulses and a thread runs from it to the shielded bits.
      // Never cut by the ladder; under reduced motion both stand still and fade in and out.
      for (const name of c.conflictsWith) {
        const orb = orbOf(c.world, name);
        if (!orb) continue;
        if (reduced) pool.spawn({ x: orb.x, y: orb.y, size: 20, life: STILL_THREAD_MS, kind: PK.ring, rgb: theme.refuse, alpha: 0.9, fade: 'edges' }, true);
        else pool.spawn({ x: orb.x, y: orb.y, size: 8, size1: 30, life: 700, kind: PK.ring, rgb: theme.refuse, alpha: 0.9 }, true);
        if (!shieldAt) continue;
        const n = Math.min(24, Math.max(6, Math.round(dist(orb, shieldAt) / 18)));
        for (let i = 1; i < n; i++) {
          const t = i / n;
          const x = orb.x + (shieldAt.x - orb.x) * t, y = orb.y + (shieldAt.y - orb.y) * t;
          if (reduced) pool.spawn({ x, y, size: 1.8, life: STILL_THREAD_MS, kind: PK.dot, rgb: theme.refuse, alpha: 0.85, fade: 'edges' }, true);
          else pool.spawn({ x, y, size: 1.8, life: 900, delay: i * 14, kind: PK.dot, rgb: theme.refuse, alpha: 0.85, fade: 'in-out' }, true);
        }
      }
      const winner = [...recent].reverse().find((r) => touching(c.world, c.key, c.slots)(r) && clock - r.at <= RECENT_MS);
      const subtitle = winner ? `sealed first${winner.seq !== null ? ` (seq ${winner.seq})` : ''} · ${whoSealed(winner.by)}` : null;
      const drop = b.source.kind === 'me' ? b.origin : shieldAt ?? at;
      for (const id of [...items.keys()]) if (id.startsWith('sen:') || id.startsWith('mk:')) items.delete(id);
      sentences.clear();
      const content: Extract<OverlayContent, { kind: 'sentence' }> = {
        kind: 'sentence', eyebrow: `REFUSED · ${c.by.toUpperCase()}`, detail: c.detail, subtitle, fixits: c.fixits, focus: c.focus === true,
        ...(c.plain ? { plain: c.plain } : {}),
      };
      sentences.set(`sen:${b.id}`, { world: c.world, key: c.key, slots: c.slots, content });
      addItem(`sen:${b.id}`, content, drop, { life: SENTENCE_MS, fadeOut: motion.quick });
      sound({ kind: 'thump' });
    } else {
      if (!reduced) burst(pool, b.pos, 6, { speed: 60, size: 2.6, life: 360, kind: PK.dot, rgb: theme.foreign, seed: hashSeed(b.id) });
      const countdown = c.retryAfter !== undefined ? `retry in ${Math.ceil(c.retryAfter)} s` : null;
      addItem(`err:${b.id}`, { kind: 'problem', title: c.status === 0 ? 'Unreachable' : `${c.status} ${c.title}`, detail: c.detail, countdown }, at, { life: Math.max(4000, (c.retryAfter ?? 0) * 1000 + 800) });
    }
  };

  // ---------- consequences and peers ----------

  const spark = (from: Pt | null, to: Pt | null, rgb: Rgb, n = 5) => {
    if (!from || !to || reduced) return;
    const d = dist(from, to);
    const dur = flightMs(d);
    for (let i = 0; i < n; i++) {
      pool.spawn({ x: from.x, y: from.y, vx: ((to.x - from.x) / dur) * 1000, vy: ((to.y - from.y) / dur) * 1000, size: 3.2 - i * 0.4, life: dur, delay: i * 40, kind: i === 0 ? PK.spark : PK.trail, rgb, alpha: 1 - i * 0.15, fade: 'none' }, i === 0);
    }
  };

  const consequence = (world: string, data: Json) => {
    const d = obj(data);
    if (!d) return;
    const wm = scene?.matter(world);
    const displaced = Array.isArray(d.displaced) ? d.displaced : [];
    let key: number | null = typeof d.key === 'number' ? d.key : null;
    if (key === null && typeof d.date === 'string') key = keyOfDate(wm, d.date, typeof d.hour === 'number' ? d.hour : undefined);
    if (key === null) key = [...recent].reverse().find((r) => r.world === world && r.kinds.includes('displace'))?.key ?? recent.filter((r) => r.world === world).pop()?.key ?? null;
    for (const entry of displaced) {
      const e = obj(entry);
      if (!e || key === null) continue;
      const from = maskFrom(e.slots);
      const off = obj(e.offered);
      let toKey: number | null = null;
      if (off && wm && typeof off.parametric === 'number' && Array.isArray(off.coordinates)) {
        const coords = off.coordinates;
        for (const pm of wm.byKey.values()) if (pm.parametric === off.parametric && pm.coordinates.length === coords.length && pm.coordinates.every((c, i) => c === coords[i])) toKey = pm.key;
      }
      const to = off ? maskFrom(off.slots) : null;
      if (from === null) continue;
      const fromSlots = slotsOfMask(from);
      const toSlots = to !== null ? slotsOfMask(to) : [];
      if (reduced) {
        if (toKey !== null && to) decorate({ world, key: toKey, slots: to, shape: SHAPE.highlight, rgb: theme.mine, alpha: 0.9, glow: 0, phase: 0 }, 1000);
        continue;
      }
      fromSlots.forEach((s, i) => {
        const a = builder.cellRect(world, key as number, s);
        const tSlot = toSlots[Math.min(i, toSlots.length - 1)];
        const b = toKey !== null && tSlot !== undefined ? builder.cellRect(world, toKey, tSlot) : null;
        if (!a) return;
        const ax = a.x + a.w / 2, ay = a.y + a.h / 2;
        const bx = b ? b.x + b.w / 2 : ax, by = b ? b.y + b.h / 2 : ay - a.h * 2;
        const dur = 420;
        for (let k = 0; k < 4; k++) {
          pool.spawn({ x: ax, y: ay, vx: ((bx - ax) / dur) * 1000, vy: ((by - ay) / dur) * 1000, size: a.w * (0.32 - k * 0.06), life: dur, delay: k * 30, kind: k === 0 ? PK.dot : PK.trail, stage: true, rgb: theme.foreign, alpha: 0.85 - k * 0.15, fade: 'none' }, k === 0);
        }
      });
      sound({ kind: 'gliss', pan: panOf(worldPoint(world)) });
    }
    const here = worldPoint(world);
    const told = Array.isArray(d.told) ? d.told : [];
    for (const t of told) {
      const e = obj(t);
      if (!e || typeof e.hand !== 'string') continue;
      const there = deps.origins.at(`world:${e.hand}`);
      const out = e.to === 'asked';
      if (out) spark(here, there, theme.ask); else spark(there, here, theme.mine);
    }
    if (typeof d.token === 'string' && typeof d.from === 'string') {
      const there = deps.origins.at(`world:${d.from}`);
      if (typeof d.state === 'string') spark(here, there, theme.mine);
      else {
        spark(there, here, theme.ask);
        sound({ kind: 'ask', pan: panOf(here) });
      }
    }
  };

  /** The field as the peers see it: where a cell is drawn, and which World and row a date names on screen. */
  const peerField: PeerField = {
    cellRect: (world, key, slot) => builder.cellRect(world, key, slot),
    find: (world, date, hour) => {
      if (!scene) return null;
      const worlds = world ? [world] : scene.layouts.map((l) => l.world);
      for (const w of worlds) {
        const key = keyOfDate(scene.matter(w), date, hour);
        if (key !== null && builder.rowsAt(w, key).length > 0) return { world: w, key };
      }
      return null;
    },
  };

  // ---------- the clock ----------

  const updateBeats = () => {
    for (const [id, b] of beats) {
      // flight
      if (b.flightAt !== null && b.dest) {
        const p = progress(clock, b.flightAt, b.flightDur);
        if (reduced) b.pos = { ...b.dest };
        else {
          const e = motion.io(p);
          b.pos = arcPoint(b.origin.x, b.origin.y, b.dest.x, b.dest.y, e);
          if (p < 1 && clock - b.lastTrail > 16) {
            b.lastTrail = clock;
            pool.spawn({ x: b.pos.x, y: b.pos.y, vx: 0, vy: 6, size: 4.2, size1: 0.8, life: 360, kind: PK.trail, rgb: mixRgb(b.tint, b.tintEnd, p), alpha: 0.75 });
          }
        }
        if (p >= 1 && b.landedAt === null) b.landedAt = b.flightAt + b.flightDur;
      }
      const landed = b.flightAt === null || b.landedAt !== null || reduced;
      // door: pending shimmer on the bits, then a latency count after 600 ms
      if (landed && b.doorAt !== null && !b.verdict?.startedAt && !b.doorShown) {
        b.doorShown = true;
        if (b.dest && !reduced) pool.spawn({ x: b.dest.x, y: b.dest.y, size: 22, size1: 8, life: 360, kind: PK.ring, rgb: b.tintEnd, alpha: 0.9 }, true);
      }
      if (b.sentAt !== null && !b.verdict?.startedAt && clock - b.sentAt >= 600 && b.dest) {
        const ms = Math.round(clock - b.sentAt);
        const text = reduced ? 'at the door…' : `${ms} ms`;
        const cur = items.get(`lat:${id}`);
        if (!cur) addItem(`lat:${id}`, { kind: 'latency', text }, b.dest, { life: Infinity });
        else if (cur.content.kind === 'latency' && cur.content.text !== text && !reduced) cur.content = { kind: 'latency', text };
      }
      // verdict
      if (b.verdict && b.verdict.startedAt === null && landed) {
        if (b.verdict.cue.kind !== 'refusal' || refusalReady(b)) startVerdict(b);
      }
      // deflect: a refused token bounces back a little before it shatters
      if (b.verdict?.startedAt != null && b.verdict.cue.kind === 'refusal' && b.dest && !reduced) {
        const p = progress(clock, b.verdict.startedAt, 220);
        const back = Math.sin(p * Math.PI) * 0.12;
        b.pos = { x: b.dest.x + (b.origin.x - b.dest.x) * back, y: b.dest.y + (b.origin.y - b.dest.y) * back };
      }
      if (b.endAt !== null && clock >= b.endAt + (b.verdict?.cue.kind === 'refusal' && !reduced ? 220 : 0)) {
        beats.delete(id);
        items.delete(`tok:${id}`);
        items.delete(`lat:${id}`);
      }
      // a lift that never flew (cancelled) fades after 8 s; a call that never hears back, after a minute
      if ((b.flightAt === null && !b.verdict && clock - b.intentAt > 8000) || clock - b.intentAt > 60_000) {
        beats.delete(id);
        items.delete(`tok:${id}`);
        items.delete(`lat:${id}`);
      }
    }
  };

  const updateSeals = () => {
    const n = backlog();
    if (n > SNAP_BACKLOG) {
      snapQueue(true);
      burstSpeed = 1;
      return;
    }
    // a burst keeps the pace it arrived with until the queue drains
    burstSpeed = queue.length === 0 ? 1 : Math.max(burstSpeed, backlogSpeed(n));
    const speed = burstSpeed;
    while (queue.length > 0) {
      const job = queue[0] as SealJob;
      if (!mayStart(job)) break;
      if (clock < nextSealAt) break;
      queue.shift();
      startSeal(job, speed);
      nextSealAt = clock + (reduced ? 0 : SEAL_SPACING / speed);
    }
  };

  const updateDims = () => {
    if (!scene) return;
    let target: string | null = null;
    for (const b of beats.values()) {
      if (b.source.kind !== 'me' || b.flightAt === null || b.verdict?.startedAt != null) continue;
      if (b.world) target = b.world;
    }
    for (const l of scene.layouts) builder.setDim(l.world, target !== null && l.world !== target ? 1 : 0);
  };

  const decorationsNow = (): Decoration[] => {
    decorations = decorations.filter((d) => clock < d.until);
    const out: Decoration[] = [];
    for (const d of decorations) {
      if (!d.pulse) { out.push(d); continue; }
      const age = clock - d.t0, left = d.until - clock;
      const phase = (age / 900) % 1;
      const fade = Math.min(1, left / 400);
      out.push({ ...d, phase: Math.round(phase * 32) / 32, alpha: d.alpha * fade });
    }
    // the door's pending shimmer: motion, not matter
    for (const b of beats.values()) {
      if (!b.bits || b.flightAt === null || b.verdict?.startedAt != null) continue;
      out.push({ world: b.bits.world, key: b.bits.key, slots: b.bits.slots, shape: SHAPE.pending, rgb: theme.ink, alpha: 1, glow: 0, phase: 0 });
    }
    // peers aiming here
    for (const [sid, p] of peers.all()) {
      if (!p.aim) { items.delete(`aim:${sid}`); continue; }
      out.push({ world: p.aim.world, key: p.aim.key, slots: p.aim.slots, shape: SHAPE.aim, rgb: viewerRgb(theme, p.hue), alpha: 1, glow: 0.2, phase: 0, inflate: 1 });
      const at = bitsCentre(p.aim.world, p.aim.key, p.aim.slots);
      if (at) {
        const text = p.name ? `${p.name} is aiming here` : 'a peer is aiming here';
        const cur = items.get(`aim:${sid}`);
        if (cur) { cur.x = at.x; cur.y = at.y; }
        else addItem(`aim:${sid}`, { kind: 'aim', text, color: toHex(viewerRgb(theme, p.hue)) }, at, { life: Infinity });
      }
    }
    return out;
  };

  const packOverlay = (): FrameModel['overlay'] => {
    const out: { id: string; x: number; y: number; scale: number; opacity: number }[] = [];
    for (const [id, it] of items) {
      // a held item stands still at the age it was held at, fully shown
      const age = (it.holdAt ?? clock) - it.t0;
      if (age >= it.life && it.holdAt === null) {
        // A refusal's sentence is not lost when its time is up: it folds to its mark on the refused bits.
        if (sentences.has(id)) foldAway(id);
        else items.delete(id);
        continue;
      }
      if (it.follow) {
        const p = it.follow();
        if (p) { it.x = p.x; it.y = p.y; }
      }
      if (it.content.kind === 'problem' && it.content.countdown !== null) {
        const m = /retry in (\d+) s/.exec(it.content.countdown);
        if (m) {
          const total = Number(m[1]);
          const left = Math.max(0, Math.ceil((it.life - 800 - age) / 1000));
          const text = `retry in ${Math.min(total, left)} s`;
          if (text !== it.content.countdown) it.content = { ...it.content, countdown: text };
        }
      }
      const held = it.holdAt !== null;
      const fin = it.fadeIn > 0 && !held ? clamp(age / it.fadeIn, 0, 1) : 1;
      const fout = Number.isFinite(it.life) && it.fadeOut > 0 && !held ? clamp((it.life - age) / it.fadeOut, 0, 1) : 1;
      const rise = Number.isFinite(it.life) ? it.rise * (age / it.life) : 0;
      out.push({ id, x: it.x, y: it.y - rise, scale: it.scale, opacity: Math.min(fin, fout) });
    }
    return out;
  };

  const packParticles = (): number => {
    let n = pool.pack(particleOut, 0);
    // tokens: a glowing core that flies, and the peers' cursors
    for (const b of beats.values()) {
      // reduced motion: no flying core, only the label fading in at the target
      if (b.bare || reduced) continue;
      if (b.verdict?.startedAt != null && b.verdict.cue.kind !== 'refusal') continue;
      const p = b.flightAt === null ? 0 : progress(clock, b.flightAt, b.flightDur);
      const rgb = mixRgb(b.tint, b.tintEnd, p);
      const fade = b.verdict?.startedAt != null ? 1 - progress(clock, b.verdict.startedAt, 220) : 1;
      const bob = b.flightAt === null ? Math.sin((clock - b.intentAt) / 120) * 2 : 0;
      const grey = b.verdict?.cue.kind === 'error';
      if (writeParticle(particleOut, n, b.pos.x, b.pos.y + bob, 7, PK.dot, grey ? theme.foreign : rgb, fade)) n++;
      if (writeParticle(particleOut, n, b.pos.x, b.pos.y + bob, 16, PK.trail, rgb, 0.35 * fade)) n++;
    }
    for (const [, p] of peers.all()) {
      if (!p.shown) continue;
      if (writeParticle(particleOut, n, p.shown.x, p.shown.y, 7, PK.cursor + 16, viewerRgb(theme, p.hue), 0.95)) n++;
    }
    return n;
  };

  /** Every World on the stage lights up in reading order, from dark rings to its bits. */
  const igniteAll = () => {
    if (!scene) return;
    const order = [...scene.layouts].sort((a, b) => a.y - b.y || a.x - b.x);
    order.forEach((L, i) => {
      const start = reduced ? 0 : 160 + i * 90;
      L.rows.forEach((r, rowIndex) => {
        for (const row of builder.rowsAt(L.world, r.key)) {
          const before = builder.capture(row.id);
          if (!before) continue;
          const slots = row.layout.slots;
          const cells = new Uint8Array(slots), delays = new Float32Array(slots);
          for (let sl = 0; sl < slots; sl++) {
            const o = sl * 12;
            const shape = (before[o + 9] as number) & 15;
            if (shape === SHAPE.free || shape === SHAPE.dot) continue;
            cells[sl] = 1;
            delays[sl] = start + (reduced ? 0 : Math.min((rowIndex + sl) * 8, 260));
            // unlit until its turn: a free ring
            before[o + 4] = theme.freeRing[0]; before[o + 5] = theme.freeRing[1]; before[o + 6] = theme.freeRing[2];
            before[o + 7] = 1; before[o + 8] = 0; before[o + 9] = SHAPE.free;
          }
          if (cells.some((v) => v)) builder.animate(row.id, { kind: reduced ? 'fade' : 'ignite', cells, delays, t0: clock, dur: reduced ? 80 : motion.reveal, before });
        }
      });
    });
  };

  const hashSeed = (s: string): number => {
    let h = 7;
    for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
    return h >>> 0;
  };

  // ---------- the public face ----------

  const play = (cue: Cue): void => {
    clock = Math.max(clock, now());
    if (hidden) {
      // the data path keeps going; what was shown snaps, and seals are counted for the away chip
      if (cue.kind === 'seal') {
        away.commits += commitsIn(cue.flips);
        if (away.flips.length < 512) away.flips.push(...cue.flips.slice(0, 512 - away.flips.length));
        for (const f of cue.flips) remember(f, cue.by);
        for (const f of cue.flips) builder.invalidate(f.world, f.key);
      } else if (cue.kind === 'receipt' && cue.head) keep(receiptHeads, cue.head, clock);
      else if (cue.kind === 'correction') for (const f of cue.flips) builder.invalidate(f.world, f.key);
      return;
    }
    switch (cue.kind) {
      case 'intent': {
        const b = beatOf(cue.id, () => newBeat(cue.id, cue.op, cue.source, cue.target, null));
        b.op = cue.op;
        if (!reduced) addItem(`tok:${b.id}`, { kind: 'token', text: tokenLabel(b), color: toHex(b.tint) }, b.pos, { life: Infinity, follow: () => b.pos });
        break;
      }
      case 'flight': {
        const b = beatOf(cue.id, () => newBeat(cue.id, cue.op, cue.source, cue.target, cue.cls));
        b.cls = cue.cls;
        b.target = cue.target ?? b.target;
        b.world = b.target?.world ?? b.world;
        b.bits = targetBits(b.target) ?? b.bits;
        b.dest = destOf(b.target) ?? (b.bits ? bitsCentre(b.bits.world, b.bits.key, b.bits.slots) : null) ?? worldPoint(b.world) ?? centre();
        b.flightAt = clock;
        b.sentAt = clock;
        b.flightDur = reduced ? 120 : flightMs(dist(b.pos, b.dest));
        b.origin = { ...b.pos };
        const label = { kind: 'token' as const, text: tokenLabel(b), color: toHex(b.tint) };
        if (reduced) addItem(`tok:${b.id}`, label, b.dest, { life: Infinity, fadeIn: 120 });
        else if (!items.has(`tok:${b.id}`)) addItem(`tok:${b.id}`, label, b.pos, { life: Infinity, follow: () => b.pos });
        const it = items.get(`tok:${b.id}`);
        if (it && reduced) it.follow = null;
        break;
      }
      case 'door': {
        const b = beats.get(cue.id);
        if (b) b.doorAt = clock;
        break;
      }
      case 'leave': {
        // Another screen: a sentence (or its mark) about some other World does not follow the visitor there.
        for (const [sen, meta] of [...sentences]) if (meta.world !== cue.world) forget(sen);
        decorations = decorations.filter((d) => d.shape !== SHAPE.shield || d.world === cue.world);
        break;
      }
      case 'withdraw': {
        const b = beats.get(cue.id);
        if (b && b.flightAt === null && !b.verdict) {
          beats.delete(cue.id);
          items.delete(`tok:${cue.id}`);
          items.delete(`lat:${cue.id}`);
        }
        break;
      }
      case 'receipt': case 'refusal': case 'error': {
        if (cue.kind === 'receipt' && cue.head) keep(byHead, cue.head, cue.id);
        let b = beats.get(cue.id);
        if (!b) {
          if (cue.kind === 'receipt') {
            // a receipt with no flight shown: still the verdict other seals wait for
            if (cue.head) keep(receiptHeads, cue.head, clock);
            break;
          }
          const where = cue.kind === 'refusal' ? bitsCentre(cue.world, cue.key, cue.slots) : null;
          const t: Target | null = cue.kind === 'refusal' ? { kind: 'bits', id: `bits:${cue.world}:${cue.key}`, world: cue.world, data: { key: cue.key, slots: `0x${cue.slots.toString(16)}` } } : null;
          b = newBeat(cue.id, cue.kind, { kind: 'unknown' }, t, null);
          b.dest = where ?? centre();
          b.bare = true;
          b.pos = { ...b.dest };
          b.origin = { ...b.dest };
          beats.set(cue.id, b);
        }
        b.verdict = { cue, arrivedAt: clock, startedAt: null };
        break;
      }
      case 'seal': {
        const worlds = new Set(cue.flips.map((f) => f.world));
        const src = cue.by.source.kind;
        const own: SealJob['own'] = OWN_KINDS.has(src) ? 'yes' : src === 'peer' ? 'no' : awaitingVerdict(worlds, cue.by.head) ? 'maybe' : 'no';
        const job: SealJob = { flips: cue.flips, by: cue.by, arrivedAt: clock, commits: commitsIn(cue.flips), own, worlds };
        for (const f of cue.flips) addPending(f);
        queue.push(job);
        break;
      }
      case 'correction':
        showFlips(cue.flips, null, 'correct');
        break;
      case 'consequence':
        consequence(cue.world, cue.data);
        break;
      case 'peer':
        peers.hear(cue, clock, peerField);
        break;
    }
  };

  const conductor: ConductorImpl = {
    play,
    frame(t: number): PackedFrame {
      clock = t;
      const dt = Math.min(100, Math.max(0, t - lastFrame));
      lastFrame = t;
      if (!hidden) {
        updateBeats();
        updateSeals();
        updateDims();
        peers.expire(clock);
        peers.follow(dt, clock, reduced, peerField, (at: Pt, p: Peer) => {
          pool.spawn({ x: at.x, y: at.y, size: 2.6, size1: 0.6, life: 380, kind: PK.trail, stage: true, rgb: viewerRgb(theme, p.hue), alpha: 0.6 });
        });
        pool.update(dt);
      }
      if (t - lastRecheck > 500) {
        lastRecheck = t;
        builder.recheck();
      }
      if (openingDue && scene && scene.layouts.length > 0) {
        // the ignition needs the rows as drawn: pack once first (a skipped version makes the renderer send everything)
        openingDue = false;
        builder.recheck();
        builder.pack(t, { particles: particleOut, particleCount: 0, ripples: ripples.out, rippleCount: 0, camera: cameraOut, overlay: [] });
        igniteAll();
      }
      builder.setDecorations(decorationsNow());
      deps.camera.pack(cameraOut, deps.dpr?.() ?? 1);
      cameraOut[CAM.reduced] = reduced ? 1 : 0;
      const overlay = packOverlay();
      const particleCount = hidden ? 0 : packParticles();
      const rippleCount = hidden ? 0 : ripples.pack(clock, (x, y) => deps.camera.toScreen(x, y));
      return builder.pack(t, { particles: particleOut, particleCount, ripples: ripples.out, rippleCount, camera: cameraOut, overlay });
    },
    snap() {
      clock = Math.max(clock, now());
      snapQueue(false);
      for (const id of [...beats.keys()]) {
        const b = beats.get(id) as Beat;
        if (b.verdict?.cue.kind === 'receipt' && b.verdict.cue.head) keep(receiptHeads, b.verdict.cue.head, clock);
      }
      beats.clear();
      pool.clear();
      ripples.clear();
      decorations = decorations.filter((d) => d.shape === SHAPE.shield);
      for (const id of [...items.keys()]) if (!id.startsWith('sen:') && !id.startsWith('mk:') && !id.startsWith('chip:')) items.delete(id);
      builder.invalidateAll();
    },
    setReduced(on: boolean) {
      if (reduced === on) return;
      reduced = on;
      pool.clear();
      if (on) ripples.clear();
      builder.invalidateAll();
    },
    get backlog() {
      return backlog();
    },
    get reduced() {
      return reduced;
    },
    get queued() {
      return queue.length;
    },
    builder,
    setScene(s: FxScene) {
      scene = s;
      return builder.setScene(s);
    },
    setTheme(t: Theme) {
      theme = t;
      motion = motionOf(t);
      builder.setTheme(t);
      // labels carry a colour; the peers' come back in the new theme's viewer lightness
      for (const id of [...items.keys()]) if (id.startsWith('aim:')) items.delete(id);
    },
    setHidden(h: boolean) {
      if (hidden === h) return;
      if (h) {
        conductor.snap();
        hidden = true;
        away = { commits: 0, flips: [] };
        return;
      }
      hidden = false;
      clock = Math.max(clock, now());
      conductor.snap();
      if (away.commits > 0) {
        const n = away.commits;
        const c = centre();
        addItem('chip:away', { kind: 'chip', text: `${n} commit${n === 1 ? '' : 's'} while you were away: replay (600 ms)`, count: n }, { x: c.x, y: stage().top + 16 }, { life: 20_000 });
      }
    },
    setQuality(q: number) {
      quality = q;
      // rung 3 and up: a quarter of the ordinary particles; essential ones (tokens, the refusal's thread) always
      pool.scale = q >= 3 ? 0.25 : 1;
      if (q >= 2) ripples.clear();
    },
    setPeer(sid, info) {
      peers.set(sid, info, clock);
      items.delete(`aim:${sid}`);
    },
    forgetPeer(sid) {
      peers.forget(sid);
      items.delete(`aim:${sid}`);
    },
    hold(id, on) {
      const it = items.get(id);
      if (!it) return;
      clock = Math.max(clock, now());
      if (on) {
        if (it.holdAt === null) it.holdAt = clock;
        return;
      }
      if (it.holdAt === null) return;
      // pick up where it was held, with at least HOLD_GRACE_MS left before the fade
      const age = Math.max(0, Math.min(it.holdAt - it.t0, it.life - HOLD_GRACE_MS));
      it.t0 = clock - age;
      it.holdAt = null;
    },
    busy(t: number): boolean {
      if (hidden) return false;
      if (beats.size > 0 || queue.length > 0 || pool.count > 0 || ripples.size > 0) return true;
      if (builder.animating(t)) return true;
      if (decorations.some((d) => d.pulse && t < d.until)) return true;
      if (peers.busy(reduced, peerField)) return true;
      for (const it of items.values()) {
        if (it.holdAt !== null) continue;
        const age = t - it.t0;
        if (it.follow || age < it.fadeIn) return true;
        if (Number.isFinite(it.life) && age > it.life - it.fadeOut) return true;
      }
      return false;
    },
    nextWake(t: number): number | null {
      if (hidden) return null;
      let at = Infinity;
      for (const d of decorations) if (d.until > t) at = Math.min(at, d.until);
      at = Math.min(at, peers.wakeAt(t));
      for (const it of items.values()) {
        if (!Number.isFinite(it.life) || it.holdAt !== null) continue;
        const fade = it.t0 + it.life - it.fadeOut;
        if (fade > t) at = Math.min(at, fade);
        if (it.content.kind === 'problem' && it.content.countdown !== null) at = Math.min(at, t + 1000 - ((t - it.t0) % 1000));
      }
      return Number.isFinite(at) ? at : null;
    },
    content: (id) => items.get(id)?.content,
    dismiss(id) {
      items.delete(id);
      if (id.startsWith('sen:')) {
        forget(id);
        decorations = decorations.filter((d) => d.shape !== SHAPE.shield);
      }
      if (id.startsWith('mk:')) forget(`sen:${id.slice(3)}`);
    },
    collapse(id) {
      if (!sentences.has(id) || !items.has(id)) return;
      clock = Math.max(clock, now());
      foldAway(id);
    },
    reopen(markId) {
      const sen = `sen:${markId.slice(3)}`;
      const meta = sentences.get(sen);
      const mark = items.get(markId);
      if (!meta || !mark) return;
      clock = Math.max(clock, now());
      items.delete(markId);
      addItem(sen, { ...meta.content, focus: false }, { x: mark.x, y: mark.y }, { life: SENTENCE_MS, fadeOut: motion.quick });
    },
    opening() {
      clock = Math.max(clock, now());
      if (!reduced && !hidden && quality < 2) {
        const c = centre();
        ripples.add({ space: 'screen', x: c.x, y: c.y, hue: 178, rgb: theme.mine, width: 180, strength: 1.2, converge: true }, clock);
      }
      openingDue = true;
    },
    replayAway() {
      items.delete('chip:away');
      const flips = away.flips;
      away = { commits: 0, flips: [] };
      if (flips.length === 0) return;
      // squeeze the whole backlog into 600 ms
      const speed = Math.max(1, motion.reveal / 600);
      showFlips(flips, null, 'replay', speed);
    },
    onSound(fn) {
      soundFns.add(fn);
      return () => soundFns.delete(fn);
    },
  };
  return conductor;
};

